/**
 * Subtitle metadata builders for booking rows: the bullet-separated text parts shown under a title,
 * the icon-chip data (rooms / places), and the per-direction flight-leg breakdown.
 *
 * Pure, platform-neutral data — each platform renders it its own way (web's `SubtitleChips` spans,
 * mobile's `BookingRow` text run) so the bookings list on web and the mobile bookings screen can't
 * describe the same item differently.
 */

import type { IconName } from '@elara/client-core/ui-contracts/icon-name';
import type { RoomSelection } from '@elara/domain/models/schemas/room-schemas';

import type { ItineraryItem } from '#models/ItineraryItemModel.js';
import type { FlightDirection } from '#models/FlightOptionModel.js';
import type { FlightSegmentModel } from '#models/FlightSegmentModel.js';

import { formatOccupancy } from '#lib/occupancy-utils.js';
import { pluralize } from '#lib/utils.js';
import { FlightSegmentModelFactory } from '#models/FlightSegmentModel.js';

/**
 * One metadata chip under a booking row's title. `tone` is semantic rather than a colour/class so
 * both platforms can map it onto their own palette (web: a Tailwind class, mobile: a theme colour).
 */
export interface SubtitleChip {
  icon: IconName;
  text: string;
  /** Positive emphasis — currently only free cancellation, which reads as reassurance. */
  tone?: 'success';
}

/** Builds the meta labels shown under a card title (rate/occupancy or dates). */
export function bookingCardSubtitleParts(item: ItineraryItem): string[] {
  const keep = (...values: Array<string | undefined | false>): string[] =>
    values.filter((v): v is string => typeof v === 'string' && v.length > 0);

  if (item.type === 'room') {
    const rate = item.rate;
    const metadata = item.metadata as RoomSelection | undefined;
    // Prefer the occupancy the guest actually selected; fall back to the
    // occupancy the matched rate was priced for, then the room's cheapest
    // rate (legacy items have no metadata.occupancy).
    const occupancy = metadata?.occupancy ?? rate?.occupancy ?? item.roomOption?.cheapestRate?.occupancy;
    const occupancyLabel = occupancy ? formatOccupancy(occupancy) : undefined;
    // Centralized on the item model so this bullet can't drift from the itinerary event detail /
    // booking-details dialog for this exact booking; falls back to the rate's static flag pre-booking.
    const refundLabel = item.cancellationDeadlineLabel ?? rate?.cancellationPolicyTitle;
    return keep(rate?.name, occupancyLabel, refundLabel);
  }

  // Flights render a multi-line subtitle instead — see flightLegs.
  if (item.type === 'flight') {
    return [];
  }

  return keep(
    item.isPlace && item.formattedStartTime ? item.formattedStartTime : undefined,
    item.formattedDateRange && item.formattedDateRange !== '-' ? item.formattedDateRange : undefined,
    item.formattedCostDetail,
  );
}

/**
 * Room metadata as icon chips (occupancy / rate / refundability), mirroring
 * the checkout page so the bookings list stays visually consistent.
 */
export function roomSubtitleChips(item: ItineraryItem): SubtitleChip[] {
  const rate = item.rate;
  const metadata = item.metadata as RoomSelection | undefined;
  const occupancy = metadata?.occupancy ?? rate?.occupancy ?? item.roomOption?.cheapestRate?.occupancy;

  const chips: SubtitleChip[] = [];
  if (occupancy) {
    const guests = occupancy.adults + (occupancy.children ?? 0);
    chips.push({ icon: guests > 1 ? 'group' : 'person', text: formatOccupancy(occupancy) });
  }
  if (rate?.name) {
    chips.push({ icon: 'price_change', text: rate.name });
  }
  // Centralized on the item model so this chip can't drift from the itinerary event detail /
  // booking-details dialog for this exact booking; falls back to the rate's static flag pre-booking,
  // and survives the rate no longer resolving live (sold out, cache expired) once booked.
  const cancellationLabel = item.cancellationDeadlineLabel ?? rate?.cancellationPolicyTitle;
  if (cancellationLabel) {
    chips.push(
      item.isFreeCancellation
        ? { icon: 'currency_exchange', text: cancellationLabel, tone: 'success' }
        : { icon: 'money_off', text: cancellationLabel },
    );
  }
  return chips;
}

/** Place / activity metadata as icon chips (when it is, then party size). */
export function placeSubtitleChips(item: ItineraryItem): SubtitleChip[] {
  const chips: SubtitleChip[] = [];

  // When the event is, as ONE chip: "2 Jul, 7:30 PM". Split across two, the reader had to stitch a
  // single fact back together from a calendar chip and a clock chip sitting side by side. The glyph
  // follows what's actually there — a calendar whenever there's a date, a clock for a time alone.
  const date = item.formattedDateRange && item.formattedDateRange !== '-' ? item.formattedDateRange : undefined;
  const time = item.isPlace && item.formattedStartTime ? item.formattedStartTime : undefined;
  const when = [date, time].filter((part): part is string => typeof part === 'string' && part.length > 0).join(', ');
  if (when) {
    chips.push({ icon: date ? 'calendar_today' : 'schedule', text: when });
  }

  if (item.formattedCostDetail) {
    chips.push({ icon: 'group', text: item.formattedCostDetail });
  }
  return chips;
}

export interface FlightLeg {
  icon: IconName;
  /** Airline logo for this leg, when the carrier provides one. */
  airlineLogoUrl?: string;
  title: string;
  parts: SubtitleChip[];
}

/**
 * The legs and passenger count of a flight, from its Elara search result or — for a flight booked
 * outside Elara, which has no FlightOption document — from the snapshot on the item.
 */
function flightLegSource(
  item: ItineraryItem,
): { segmentsFor: (direction: FlightDirection) => FlightSegmentModel[]; totalPassengers: number } | undefined {
  const flight = item.flightOption;
  if (flight) {
    return {
      segmentsFor: (direction) => flight.flightsForDirection(direction),
      totalPassengers: flight.totalPassengers,
    };
  }

  const external = item.externalFlight;
  if (!external) return undefined;
  const { adults = 1, children = 0, infants = 0 } = external.passengers ?? {};
  return {
    segmentsFor: (direction) =>
      FlightSegmentModelFactory.fromMany(
        direction === 'outbound'
          ? external.outboundSegments ?? external.segments ?? []
          : external.returnSegments ?? [],
      ),
    totalPassengers: adults + children + infants,
  };
}

/**
 * Splits a flight into its directional legs, each with its own icon, title
 * (city/airport route) and subtitle parts (times, stops, flight numbers,
 * airline). A one-way flight yields a single leg; a round trip yields the
 * outbound (takeoff) then the return (landing).
 */
export function flightLegs(item: ItineraryItem): FlightLeg[] {
  const flight = flightLegSource(item);
  if (!flight) return [];

  const build = (direction: FlightDirection, icon: IconName): FlightLeg | undefined => {
    const segments = flight.segmentsFor(direction);
    const first = segments[0];
    if (!first) return undefined;
    const last = segments.at(-1) ?? first;

    const from = `${first.origin.city_name ?? first.origin.airport_name} (${first.originCode})`;
    const to = `${last.destination.city_name ?? last.destination.airport_name} (${last.destinationCode})`;
    const time = `${first.formattedDepartureDate}, ${first.formattedDepartureTime} – ${last.formattedArrivalTime}`;
    const flightNumbers = segments
      .map((s) => s.flightNumber)
      .filter(Boolean)
      .join(', ');
    const stops = segments.length - 1;
    const stopsLabel = stops === 0 ? 'Nonstop' : `${stops} ${pluralize(stops, 'stop')}`;
    const passengers = `${flight.totalPassengers} ${pluralize(flight.totalPassengers, 'passenger')}`;

    const parts: SubtitleChip[] = [
      { icon: 'schedule', text: time },
      { icon: 'airline_stops', text: stopsLabel },
      flightNumbers ? { icon: 'airplane_ticket' as IconName, text: flightNumbers } : undefined,
      { icon: 'group', text: passengers },
    ].filter((v): v is SubtitleChip => v !== undefined);
    return { icon, airlineLogoUrl: first.airlineLogoUrl, title: `${from} → ${to}`, parts };
  };

  return [build('outbound', 'flight_takeoff'), build('return', 'flight_land')].filter(
    (v): v is FlightLeg => v !== undefined,
  );
}
